import {
  AppLogo,
  Button,
  ButtonsContainer,
  ButtonWithoutIcon,
  FormField,
  Heading,
  Icon,
  LegendMenu,
  LegendMenuProps,
  Link,
  LoadingIndicator,
  MAP_NAVIGATION_BREAKPOINT,
  useMediaQuery,
} from '@msaf/core-react'
import { RequestOptions } from '@msaf/generic-search-common'
import { goToLatLng, MAP_DEFAULTS } from '@msaf/maps-common'
import { LeafletMap, MapPanels, MarkerPanelStates } from '@msaf/maps-react'
import classNames from 'classnames'
import * as L from 'leaflet'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MAP_SEARCH_APP_LOGO } from '../../../../../../../constants'
import { BackLink } from '../../../../../../../types/routing-types'
import MapMenu, { MapPanelDisplayOptions } from '../../../../../aoraki/core-react/mapping/map-menu'
import MapPanel from '../../../../../map-panel'
import { SearchTemplate, SearchViewColumn } from '../../../../generic-search-common/src'
import {
  CommonAdvancedSearchProps,
  getSearchTemplateWithDisplayFilters,
  useGetSearchConfig,
  useSearch,
} from '../../../../generic-search-react/src'
import { MapContextPanel } from '../../../../maps-react/src/map-page/components/map-context-panel'
import useMapSearch, { CustomIconFunction } from '../../hooks/use-map-search'
import CustomZoomControl from '../custom-zoom-control'
import GotoControl, { AddressOption, LocationMetaData } from '../map-goto-control'
import { WellsMapPanelContent } from '../map-panel-content'
import MapSearchPanel from '../map-search-panel'
import { SearchMarkerPanel } from '../search-marker-panel'

export interface MapTabProps {
  label: string
  searchTypeKey: string
}

export interface AdvancedSearchMapProps extends CommonAdvancedSearchProps {
  customPopupComponent?: React.ElementType
  defaultIcon: L.Icon<L.IconOptions>
  locationIcon?: L.Icon<L.IconOptions> | L.DivIcon
  mapOptions?: L.MapOptions
  listSearchRoute?: string
  getBaseLayers?: () => L.LayerGroup<any>
  customActions?: { [key: string]: (...args: any[]) => void }
  addInitialLayersAndMarkers?: (map: L.Map) => void
  mapPanelDisplayOptions?: MapPanelDisplayOptions
  pageHeaderActionsConfig?: Array<ButtonWithoutIcon>
  showMapPageHeader?: boolean
  isDefaultTabbedContent?: boolean
  onTabChange?: (index: number) => void
  mapTabs?: Array<MapTabProps>
  activeTab?: number
  onZoomLevelChange?: (map: L.Map) => void
  cachedMapBounds?: { northEast: L.LatLng; southWest: L.LatLng }
  legendMenuConfig?: LegendMenuProps
  customIconFunction?: CustomIconFunction
}

export default function WellsAdvancedSearchMap({
  searchTypeKey,
  onSearchQueryChange,
  requestOptions,
  customActions,
  searchTemplate,
  defaultIcon,
  locationIcon,
  mapOptions = {},
  pageSize = 100000, // no limit by default
  dateFormat,
  getBaseLayers,
  addInitialLayersAndMarkers,
  listSearchRoute,
  mapPanelDisplayOptions = {},
  filterBy,
  pageHeaderActionsConfig,
  showMapPageHeader = false,
  cachedSearchQuery,
  onZoomLevelChange,
  isDefaultTabbedContent,
  onTabChange,
  mapTabs,
  activeTab,
  cachedMapBounds,
  legendMenuConfig,
  customIconFunction,
}: AdvancedSearchMapProps) {
  const isDesktop = useMediaQuery(`(min-width: ${MAP_NAVIGATION_BREAKPOINT})`)

  const { state: locationState } = useLocation() as { state: { selectedAddress: AddressOption; backLink?: BackLink } }

  const [map, setMapState] = useState<L.Map | undefined>()
  const [appliedFilterCount, setAppliedFilterCount] = useState(0)
  const [activeSearchTemplate, setActiveSearchTemplate] = useState<SearchTemplate | undefined>(searchTemplate)
  const [mapDetailHeadingCol, setMapDetailHeadingCol] = useState<SearchViewColumn>()
  const [activePanel, setActivePanel] = useState<MapPanels | false>(MapPanels.SEARCH)
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false)
  const [backLink, setBackLink] = useState<BackLink>({
    path: '/',
    label: 'Home',
  })

  const setMap = (map: L.Map | undefined) => {
    setMapState(map)
  }

  // Save the address passed in with the `location` state
  // An address can be passed to the map from a address search interface in a different route
  // and will be available when this component first mounts.
  // We do not need to listen for further changes using `useEffect`
  const [locationStateAddress] = useState(locationState?.selectedAddress)

  useEffect(() => {
    if (locationState?.backLink) {
      setBackLink(locationState.backLink)
    } else if (listSearchRoute) {
      setBackLink({
        path: listSearchRoute,
        label: 'View list search',
      })
    }
  }, [listSearchRoute, locationState])

  useEffect(() => {
    if (searchTemplate) {
      // Filter out the hidden filters so that the rest of the logic need not worry about it
      const template = getSearchTemplateWithDisplayFilters(searchTemplate)
      setActiveSearchTemplate(template)
    }
  }, [searchTemplate])

  const searchRequestOptions: RequestOptions = {
    ...requestOptions,
    urlPrefix: `${requestOptions.urlPrefix}/generic-search`,
  }

  const { isSuccess, data } = useGetSearchConfig(
    searchTypeKey,
    requestOptions,
    !!searchTypeKey && !activeSearchTemplate,
  )

  useEffect(() => {
    if (isSuccess && data?.searchTemplate) {
      // Filter out the hidden filters so that the rest of the logic need not worry about it
      const searchTemplate: SearchTemplate = data?.searchTemplate
      const template = getSearchTemplateWithDisplayFilters(searchTemplate)
      setActiveSearchTemplate(template)
    }
  }, [isSuccess, data?.searchTemplate])

  const { searchQuery, searchResults, updateQuery, isSearching } = useSearch({
    searchTemplate: activeSearchTemplate,
    pageSize,
    requestOptions: searchRequestOptions,
    viewKey: activeSearchTemplate?.mapConfig?.key,
    onSearchQueryChange,
    filterBy,
    cachedSearchQuery,
  })

  const {
    viewAction,
    nextAction,
    previousAction,
    setMarkerPanelState,
    showNextButton,
    popupData,
    markerPanelState,
    resetActiveMarker,
  } = useMapSearch({
    map,
    searchTypeKey,
    activeSearchTemplate,
    defaultIcon,
    searchResults,
    searchRequestOptions,
    cachedMapBounds,
    customIconFunction,
    appliedFilterCount,
  })

  useEffect(() => {
    const count = searchQuery?.filterSet.filters.reduce((count, filter) => {
      if ('filterSet' in filter) {
        return count + filter.filterSet.filters.length
      }
      return count
    }, 0)
    setAppliedFilterCount(count || 0)
  }, [searchQuery])

  useEffect(() => {
    if (map && !isSearching && locationStateAddress && activePanel !== MapPanels.LOCATION) {
      map.whenReady(function () {
        // Wait until map has initialized before trying to go to address in locationStateAddress
        // setTimeout appears to prevent a race condition where the map sometimes is set to
        // default zoom/location after going to locationStateAddress
        setTimeout(function () {
          setActivePanel(MapPanels.LOCATION)
        })
      })
    }

    if (map) {
      // Perform additional action each time the zoom level changes
      // `onZoomLevelChange` currently just saves the current map bounds in a cache
      map.on('zoomend', function () {
        onZoomLevelChange && onZoomLevelChange(map)
      })
    }
    // Excluded `onZoomLevelChange` function from dependencies to avoid re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isSearching, locationState])

  const mapDetailView = activeSearchTemplate?.views.find(
    ({ viewKey }) => viewKey === activeSearchTemplate?.mapConfig?.detailKey,
  )

  const defaultMapColumns = useMemo(
    () => mapDetailView?.table.columns.filter((col) => col.type !== 'hidden'),
    [mapDetailView?.table.columns],
  )

  useEffect(() => {
    setMapDetailHeadingCol(defaultMapColumns?.find((col) => col.isMapDetailHeading))
  }, [defaultMapColumns])

  const mapColumns = useMemo(() => defaultMapColumns?.filter((col) => col.isMapDetail), [defaultMapColumns])

  // Check if the search template and search query are loaded
  if (!activeSearchTemplate || !searchQuery) {
    return <LoadingIndicator>Loading&hellip;</LoadingIndicator>
  }

  const actionConfig = mapDetailView?.table.onRowClick

  const mapCreateOptions = {
    center: MAP_DEFAULTS.DEFAULT_CENTER,
    crs: MAP_DEFAULTS.CRS,
    ...mapOptions,
    ...(getBaseLayers && { baseLayers: getBaseLayers() }),
  }

  /*
    Update which panel content is active and whether panel is in collapsed state. State handled together
    due to linked behaviour and slight variations between mobile/desktop. See Storybook
    for more information regarding expected behaviour.
  */
  const updatePanelState = (isCollapsed: boolean, currentPanel?: MapPanels | false) => {
    /*
      If specified, switch which panel is currently active (otherwise current activePanel value is retained).
      May be set to false via mobile UI (as menu buttons need to reflect that panel entirely hidden/inactive
      rather than just collapsed in this case).
    */
    if (currentPanel !== undefined) {
      setActivePanel(currentPanel)
    }
    setIsPanelCollapsed(isCollapsed)
  }

  const contextPanelClasses = classNames('l-map-page__context-panel', {
    'l-map-page__context-panel--open': markerPanelState !== MarkerPanelStates.HIDDEN,
  })

  const panelsContainerClasses = classNames('l-map-page__panels', {
    'l-map-page__panels--open': !!isPanelCollapsed,
    'l-map-page__panels--no-header': !showMapPageHeader,
  })

  const HeadingAction = () => {
    return (
      <>
        <Heading level={2}>
          {mapDetailHeadingCol && popupData?.[mapDetailHeadingCol.elementKey]
            ? popupData?.[mapDetailHeadingCol.elementKey]
            : 'Details'}
        </Heading>
        {actionConfig && typeof actionConfig['type'] === 'string' && (
          <Button
            label={actionConfig.label}
            buttonStyle='text-action'
            iconAriaLabel={actionConfig.label}
            path={viewAction(actionConfig, customActions) ?? ''}
          />
        )}
      </>
    )
  }

  return (
    <div className='l-map-page'>
      <div className='l-map-page__menus'>
        {/* Nav bar for switching out active panel content */}
        <div className='l-map-page__nav'>
          <MapMenu
            updatePanelState={updatePanelState}
            activePanel={activePanel}
            appliedFilterCount={appliedFilterCount}
            appLogo={<AppLogo {...MAP_SEARCH_APP_LOGO} isIconOnlyVersion isSkeleton={isSearching} />}
            {...mapPanelDisplayOptions}
          />
        </div>

        <div className={`l-map-page__panels ${showMapPageHeader ? 'l-map-page__panels-with-header' : ''}`}>
          {/* Container for main panel and marker panel (which is only visible when map marker selected) */}
          {/* Marker panel rendered inside MapPanel component on desktop for positioning */}
          <MapPanel
            updatePanelState={updatePanelState}
            isCollapsed={isPanelCollapsed}
            markerPanelComponent={
              <div className={contextPanelClasses}>
                <MapContextPanel
                  isPanelOpen={markerPanelState !== MarkerPanelStates.HIDDEN}
                  setIsPanelOpen={(isOpen) => {
                    if (!isOpen) {
                      setMarkerPanelState(MarkerPanelStates.HIDDEN)
                      resetActiveMarker()
                    }
                  }}
                  contextPanelConfig={{
                    headingAction: <HeadingAction />,
                    panelContent: (
                      <SearchMarkerPanel
                        mapDetailHeadingCol={mapDetailHeadingCol}
                        config={mapColumns}
                        data={popupData}
                        viewAction={viewAction}
                        customActions={customActions}
                        previousAction={previousAction}
                        nextAction={nextAction}
                        showNextButton={showNextButton}
                      />
                    ),
                  }}
                />
              </div>
            }
          >
            {/* Search panel defaults to active panel on desktop */}
            {(activePanel === MapPanels.SEARCH || !activePanel) && (
              <WellsMapPanelContent heading={MapPanels.SEARCH}>
                <MapSearchPanel
                  activeSearchTemplate={activeSearchTemplate}
                  searchTypeKey={searchTypeKey}
                  updateQuery={updateQuery}
                  requestOptions={requestOptions}
                  searchQuery={searchQuery}
                  dateFormat={dateFormat}
                  isLoading={isSearching}
                  isDefaultTabbedContent={isDefaultTabbedContent}
                  onTabChange={onTabChange}
                  mapTabs={mapTabs}
                  activeTab={activeTab}
                />
              </WellsMapPanelContent>
            )}
            {activePanel === MapPanels.LOCATION && (
              <WellsMapPanelContent heading={MapPanels.LOCATION}>
                <FormField label='Address' labelId='gotoAddress'>
                  <GotoControl
                    selectedAddress={locationStateAddress}
                    requestOptions={requestOptions}
                    goToLocation={({ lat, lng, extents }: LocationMetaData) =>
                      map && goToLatLng(map, lat, lng, extents, locationIcon)
                    }
                  />
                </FormField>
              </WellsMapPanelContent>
            )}
            {activePanel === MapPanels.FEATURES && (
              <WellsMapPanelContent heading={MapPanels.FEATURES}>{/* TODO - pending design */}</WellsMapPanelContent>
            )}
            {activePanel === MapPanels.LAYERS && (
              <WellsMapPanelContent heading={MapPanels.LAYERS}>{/* TODO - pending design */}</WellsMapPanelContent>
            )}
          </MapPanel>

          {/* Transparent overlay displayed when main panel open on mobile. Clicking overlay closes panel */}
          {!isDesktop && !isPanelCollapsed && (
            <div className='c-map-panel-overlay' onClick={() => updatePanelState(true, false)} />
          )}

          {/* Marker panel needs to be output here instead of inside MapPanel on mobile for positioning */}
          {!isDesktop && (
            <div className={panelsContainerClasses}>
              <div className={contextPanelClasses}>
                <MapContextPanel
                  isPanelOpen={markerPanelState !== MarkerPanelStates.HIDDEN}
                  setIsPanelOpen={(isOpen) => {
                    if (!isOpen) {
                      setMarkerPanelState(MarkerPanelStates.HIDDEN)
                      resetActiveMarker()
                    }
                  }}
                  contextPanelConfig={{
                    headingAction: <HeadingAction />,
                    panelContent: (
                      <SearchMarkerPanel
                        mapDetailHeadingCol={mapDetailHeadingCol}
                        config={mapColumns}
                        data={popupData}
                        viewAction={viewAction}
                        customActions={customActions}
                        previousAction={previousAction}
                        nextAction={nextAction}
                        showNextButton={showNextButton}
                      />
                    ),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='l-map-page__map-area'>
        {showMapPageHeader && (
          <div className='l-map-page__header'>
            {backLink && (
              <Link className='c-back-link' path={backLink.path} exact={true}>
                <Icon className='c-back-link__icon' icon='chevron-left' ariaHidden={true} />
                <span className='c-back-link__text'>{backLink.label}</span>
              </Link>
            )}
            {pageHeaderActionsConfig && (
              <ButtonsContainer containerStyle='right'>
                {pageHeaderActionsConfig.map((props: ButtonWithoutIcon) => (
                  <Button {...props} />
                ))}
              </ButtonsContainer>
            )}
          </div>
        )}
        <div className={`l-map-page__map-controls ${showMapPageHeader ? 'l-map-page__map-controls-with-header' : ''}`}>
          {!showMapPageHeader && (
            <Button
              label='View list search'
              icon='table'
              path={listSearchRoute ?? activeSearchTemplate?.mapConfig?.tabularViewEmberRoute ?? '/'}
              buttonStyle='tertiary'
              iconAlignment='left'
            />
          )}
          <CustomZoomControl
            zoomControlOptions={[
              {
                id: 'zoom-in',
                iconType: 'add',
                iconAriaLabel: 'Zoom in',
                zoomAction: () => map?.setZoom(map.getZoom() + 1),
                noop: (event) => event.stopPropagation(),
              },
              {
                id: 'zoom-out',
                iconType: 'subtract',
                iconAriaLabel: 'Zoom out',
                zoomAction: () => map?.setZoom(map.getZoom() - 1),
                noop: (event) => event.stopPropagation(),
              },
            ]}
          />
        </div>

        <div className='l-map-page__legend'>{legendMenuConfig && <LegendMenu {...legendMenuConfig} />}</div>

        <LeafletMap
          options={mapCreateOptions}
          setMap={setMap}
          addInitialLayersAndMarkers={addInitialLayersAndMarkers}
        />
      </div>
    </div>
  )
}
